import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Divider,
  Pagination,
  Stack,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Search, FileDownload, Print, PictureAsPdf } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { fetchPatients, fetchDocuments, fetchDocumentBlob, searchPatients } from '../api';

const DocumentLink = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.5),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const SearchButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  width: '100%',
}));

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: '40px !important',
    minHeight: '40px !important',
  },
  '& .MuiInputBase-root': {
    height: '40px !important',
    minHeight: '40px !important',
  },
  '& .MuiInputBase-input': {
    height: 'auto !important',
    padding: '8.5px 14px !important',
  },
}));

/**
 * Adjusts and formats UTC timestamps that were incorrectly stored 
 * (Eastern time stored as if it were UTC)
 * 
 * @param {string} dateStr - ISO date string from backend
 * @param {object} formatOptions - Options for date formatting
 * @param {boolean} timeOnly - If true, only returns time portion
 * @param {boolean} isCalendarDate - If true, treats as calendar date without timezone adjustment
 * @returns {string} Correctly formatted date/time in Eastern timezone
 */
const formatFixedEastern = (dateStr, formatOptions = {}, timeOnly = false, isCalendarDate = false) => {
  if (!dateStr) return '';
  
  // For dates that should be treated as calendar dates only (like DOB and admit date)
  if (isCalendarDate) {
    // Parse the date without timezone adjustments
    const dateParts = dateStr.split(/[-/T]/);
    let year, month, day;
    
    // Handle different date formats: YYYY-MM-DD, MM/DD/YYYY, etc.
    if (dateStr.includes('/')) {
      // MM/DD/YYYY format
      month = parseInt(dateParts[0], 10) - 1; // Convert to 0-based month
      day = parseInt(dateParts[1], 10);
      year = parseInt(dateParts[2], 10);
    } else {
      // YYYY-MM-DD format
      year = parseInt(dateParts[0], 10);
      month = parseInt(dateParts[1], 10) - 1; // Convert to 0-based month
      day = parseInt(dateParts[2], 10);
    }
    
    // Create date object and format it
    const localDate = new Date(year, month, day);
    return localDate.toLocaleDateString('en-US', formatOptions);
  }
  
  // Original code for datetime values with timezone adjustment
  const date = new Date(dateStr);
  
  // Determine if date is within US Eastern Daylight Time
  // DST begins on second Sunday in March and ends on first Sunday in November
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // Jan = 1, Dec = 12
  const day = date.getUTCDate();
  
  // Check if date is within DST period
  let isDST = false;
  
  if (month > 3 && month < 11) {
    // April through October are definitely DST
    isDST = true;
  } else if (month === 3) {
    // March - DST starts on second Sunday
    const secondSunday = 8 + (7 - new Date(year, 2, 8).getDay()) % 7;
    isDST = day >= secondSunday;
  } else if (month === 11) {
    // November - DST ends on first Sunday
    const firstSunday = 1 + (7 - new Date(year, 10, 1).getDay()) % 7;
    isDST = day < firstSunday;
  }
  
  // Add offset (4 hours during DST, 5 hours during standard time)
  const correctedDate = new Date(date.getTime() + (isDST ? 4 : 5) * 60 * 60 * 1000);
  
  // Format with user's locale (defaulting to en-US)
  const options = {
    timeZone: 'America/New_York',
    ...formatOptions
  };
  
  if (timeOnly) {
    return correctedDate.toLocaleTimeString('en-US', options);
  }
  
  return correctedDate.toLocaleString('en-US', options);
};

function PatientDocuments({ patientId, onSelectDocument, selectedDocument }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    if (patientId) {
      fetchDocuments(patientId)
        .then((data) => {
          if (!mounted) return;
          if (data && data.documents) {
            setDocuments(data.documents);
          } else {
            setError('Unknown error or no data returned.');
          }
        })
        .catch((err) => {
          setError(err.message || 'Failed to load documents');
        })
        .finally(() => {
          if (mounted) setLoading(false);
        });
    }
    return () => { mounted = false; };
  }, [patientId]);

  // Format the filename to only show scan date and ID
  const formatFileName = (fileName) => {
    if (!fileName) return 'Unknown';
    
    // Check if it's the expected format with date/timestamp pattern
    const datePattern = /_(\d{8}_\d{6})pdf\.pdf$/;
    const match = fileName.match(datePattern);
    
    if (match && match[1]) {
      // Return just the timestamp with .pdf extension
      return `${match[1]}.pdf`;
    }
    
    // Alternative pattern with just a timestamp anywhere in the name
    const altPattern = /(\d{8}_\d{6})/;
    const altMatch = fileName.match(altPattern);
    
    if (altMatch && altMatch[1]) {
      return `${altMatch[1]}.pdf`;
    }
    
    // Fall back to original name but remove duplicate pdf extensions
    return fileName.replace(/pdf\.pdf$/, '.pdf');
  };

  if (loading) return <Typography variant="body2" color="text.secondary">Loading documents...</Typography>;
  if (error) return <Typography variant="body2" color="error">{error}</Typography>;
  if (!documents.length) return <Typography variant="body2" color="text.secondary">No documents found</Typography>;

  return (
    <List dense disablePadding>
      {documents.map((doc, index) => (
        <React.Fragment key={doc.id}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DocumentLink 
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling to parent
                onSelectDocument(doc);
              }} 
              sx={{ flexGrow: 1 }}
            >
              <ListItemText 
                primary={formatFileName(doc.file_name) || `Document ${index + 1}`} 
                primaryTypographyProps={{ 
                  color: 'primary', 
                  style: {
                    textDecoration: 'underline',
                    fontWeight: selectedDocument && doc.id === selectedDocument.id ? 'bold' : 'normal'
                  }
                }}
                secondary={doc.upload_date ? formatFixedEastern(doc.upload_date, { year: 'numeric', month: 'short', day: 'numeric' }) : null}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </DocumentLink>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ ml: 1, minWidth: '60px', textAlign: 'right', whiteSpace: 'nowrap' }}
            >
              ({doc.page_count || '1'} {doc.page_count === 1 ? 'page' : 'pages'})
            </Typography>
          </Box>
          {index < documents.length - 1 && <Divider component="li" />}
        </React.Fragment>
      ))}
    </List>
  );
}

const DocumentViewer = ({ document }) => {
  const [blob, setBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchDocument() {
      if (!document) return;
      
      setLoading(true);
      setError(null);
      try {
        const documentBlob = await fetchDocumentBlob(document.b2_file_id);
        setBlob(documentBlob);
      } catch (err) {
        setError(`Failed to load document: ${err.message}`);
        // Log errors to a file - implementing this safely for HIPAA compliance
        // Using a simple logger utility that will write to a secure log file
        try {
          // This would normally be an imported secure logging service
          // For now we'll create a secure log entry object to be processed by a logging service
          const logEntry = {
            timestamp: new Date().toISOString(),
            type: 'error',
            message: `Error loading document: ${err.message}`,
            // No PHI is included in the log
            documentId: document?.id ? `[ID:${document.id}]` : 'unknown',
            userAgent: navigator.userAgent
          };
          
          // In a real implementation, this would be sent to a secure logging endpoint
          // Example: await secureLogger.log(logEntry);
          
          // Store in sessionStorage temporarily (will be cleared when browser closes)
          const logs = JSON.parse(sessionStorage.getItem('appLogs') || '[]');
          logs.push(logEntry);
          sessionStorage.setItem('appLogs', JSON.stringify(logs));
        } catch (logError) {
          // Silently fail if logging itself fails
        }
      } finally {
        setLoading(false);
      }
    }
    
    fetchDocument();
  }, [document]);

  const handleDownload = () => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = document.file_name || `document_${document.id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.onload = function() {
        newWindow.print();
      };
    }
    // Don't revoke URL immediately as it's needed for printing
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <IconButton onClick={handleDownload} disabled={!blob} title="Download">
          <FileDownload />
        </IconButton>
        <IconButton onClick={handlePrint} disabled={!blob} title="Print">
          <Print />
        </IconButton>
      </Box>

      <Paper 
        elevation={0} 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          p: 3,
          bgcolor: 'grey.100'
        }}
      >
        {loading ? (
          <Typography>Loading document...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !document ? (
          <>
            <Box sx={{ mb: 2, opacity: 0.5 }}>
              <PictureAsPdf sx={{ fontSize: 80, color: 'grey.500' }} />
            </Box>
            <Typography variant="h6">No Document Selected</Typography>
            <Typography color="text.secondary" align="center">
              Choose a patient from the left panel to view their documents
            </Typography>
          </>
        ) : !blob ? (
          <Typography>No document data available</Typography>
        ) : (
          <iframe 
            src={URL.createObjectURL(blob)} 
            title={document.file_name || `Document #${document.id}`}
            style={{ width: '100%', height: '100%', border: 'none', minHeight: '500px' }}
          />
        )}
      </Paper>
    </Box>
  );
};

// Map user IDs to hospital names (same mapping as backend)
const getUserIdToHospitalName = (userId) => {
  const hospitalMapping = {
    2: 'Christ (Jersey City)',
    3: 'HUMC (Hoboken)',
    4: 'Bayonne',
    5: 'HUMC (Hoboken)'
  };
  return hospitalMapping[userId] || 'Unknown';
};

// Determine hospital for a patient based on their documents
const getPatientHospital = (patient) => {
  // If patient has documents loaded, use the first document's scanned_by_user_id
  if (patient.documents && patient.documents.length > 0) {
    const firstDoc = patient.documents[0];
    if (firstDoc.scanned_by_user_id) {
      return getUserIdToHospitalName(firstDoc.scanned_by_user_id);
    }
  }
  
  // If no documents or scanned_by_user_id, try to get from patient object itself
  if (patient.scanned_by_user_id) {
    return getUserIdToHospitalName(patient.scanned_by_user_id);
  }
  
  // If still no hospital info, return 'Unknown'
  return 'Unknown';
};

const PatientScans = ({ userId, user }) => {
  const [patients, setPatients] = useState([]);
  const [allPatients, setAllPatients] = useState([]); // Store all fetched patients
  const [filteredPatients, setFilteredPatients] = useState([]); // Store filtered results
  const [searchTerm, setSearchTerm] = useState('');
  const [dobDate, setDobDate] = useState(null);
  const [admitDate, setAdmitDate] = useState(null);
  const [hospital, setHospital] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPatients, setTotalPatients] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSanduke, setShowSanduke] = useState(false);

  const isAdmin = useMemo(() => {
    const groups = user?.profile?.['cognito:groups']?.map(g => g.toLowerCase()) || [];
    return groups.includes('admin') || String(user?.profile?.sub) === '1';
  }, [user]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Fetch patients on initial load or page change (when not searching)
  useEffect(() => {
    if (!isSearching) {
      const excludeUserId = showSanduke ? null : 1;
      fetchPatients(page, itemsPerPage, { excludeUserId })
        .then((data) => {
          if (data && data.patients) {
            setPatients(data.patients);
            
            // Store all patients for searching
            setAllPatients(prevPatients => {
              const newAllPatients = [...prevPatients];
              data.patients.forEach(patient => {
                if (!newAllPatients.some(p => p.id === patient.id)) {
                  newAllPatients.push(patient);
                }
              });
              return newAllPatients;
            });
            
            // Set total pages/counts
            setTotalPages(data.pages || 1);
            
            // Check if the API returns total count directly
            if (data.total_count !== undefined) {
              setTotalPatients(data.total_count);
            } else if (data.pages) {
              // If we don't have a direct total, estimate based on pages
              setTotalPatients(data.pages * itemsPerPage);
            }
          }
        })
        .catch(err => {
          // Log error to file as per HIPAA requirements
          logError('Error fetching patients', err);
        });
    }
  }, [page, isSearching, showSanduke]);  // Re-run if page, search status, or sanduke toggle changes
  
  // Function to safely log errors (HIPAA compliant)
  const logError = (message, error) => {
    try {
      const logEntry = {
        timestamp: new Date().toISOString(),
        message,
        errorMessage: error?.message || 'Unknown error',
      };
      
      // Store in sessionStorage temporarily
      const logs = JSON.parse(sessionStorage.getItem('appLogs') || '[]');
      logs.push(logEntry);
      sessionStorage.setItem('appLogs', JSON.stringify(logs));
    } catch (e) {
      // Silent fail if logging itself fails
    }
  };

  const formatDate = (dateString) => {
    // Treat DOB and admit date as calendar dates without timezone adjustment
    return formatFixedEastern(
      dateString, 
      { year: 'numeric', month: 'numeric', day: 'numeric' },
      false,
      true // Mark as calendar date
    );
  };
  
  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
  };
  
  // Handle patient selection without auto-loading the first document
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSelectedDocument(null); // Clear current document
    // No automatic document selection
  };

  const handlePageChange = (event, value) => {
    // Store the current page value to avoid flickering
    setCurrentPage(value);
    
    if (isSearching) {
      // When searching, handle pagination for filtered results
      const startIndex = (value - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setPatients(filteredPatients.slice(startIndex, endIndex));
    } else {
      // Normal pagination through API
      setPage(value);
    }
    // Reset selected items
    setSelectedPatient(null);
    setSelectedDocument(null);
  };
  
  // Handle search form submission
  const handleSearch = async (e) => {
    e.preventDefault();

    // If all search fields are empty, reset to normal pagination mode
    if (!searchTerm && !dobDate && !admitDate && !hospital) {
      setIsSearching(false);
      setFilteredPatients([]);
      setCurrentPage(1);
      setPatients(allPatients.slice(0, itemsPerPage));
      setTotalPatients(allPatients.length);
      setTotalPages(Math.ceil(allPatients.length / itemsPerPage) || 1);
      return;
    }

    setIsLoading(true);
    try {
      // Format dates for API (YYYY-MM-DD)
      const formatDateForAPI = (date) => {
        if (!date) return '';
        
        // If it's already a string in YYYY-MM-DD format, return as is
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
          return date;
        }
        
        // Otherwise format the date object
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      };
      
      // Query backend for patient search
      const excludeUserId = showSanduke ? null : 1;
      const response = await searchPatients({
        name: searchTerm,
        dob: formatDateForAPI(dobDate),
        admit_date: formatDateForAPI(admitDate),
        hospital: hospital,
        exclude_scanned_by_user_id: excludeUserId
      });
      const results = response.patients || [];
      setFilteredPatients(results);
      setTotalPatients(results.length);
      const filteredPages = Math.ceil(results.length / itemsPerPage) || 1;
      setTotalPages(filteredPages);
      setCurrentPage(1);
      setPatients(results.slice(0, itemsPerPage));
      setIsSearching(true);
    } catch (err) {
      // Optionally log error to file (HIPAA compliant)
      if (typeof logError === 'function') {
        logError('Failed to search patients', err);
      }
      setFilteredPatients([]);
      setTotalPatients(0);
      setTotalPages(1);
      setPatients([]);
      setIsSearching(true);
    } finally {
      setIsLoading(false);
    }
  }; 
  
  // Reset search and return to normal pagination mode
  const handleResetSearch = () => {
    setSearchTerm('');
    setDobDate(null);
    setAdmitDate(null);
    setHospital('');
    setIsSearching(false);
    setFilteredPatients([]);
    setCurrentPage(1);
    setSelectedDocument(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 4, height: 'calc(100vh - 100px)' }}>
        <Grid container spacing={2} sx={{ height: '100%' }}>
          {/* Left Panel - Patient Search and Documents */}
          <Grid item xs={12} md={4} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{ p: 2, mb: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>Patient Records</Typography>
              
              {/* Search Form */}
              <Box component="form" onSubmit={handleSearch} noValidate sx={{ mb: 2 }}>
                <TextField
                  label="Search by name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setSelectedDocument(null);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      height: '40px !important',
                      minHeight: '40px !important'
                    },
                    '& .MuiInputBase-root': {
                      height: '40px !important',
                      minHeight: '40px !important'
                    },
                    '& .MuiInputBase-input': {
                      height: 'auto !important',
                      padding: '8.5px 14px !important'
                    }
                  }}
                />
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <StyledDatePicker
                      label="Date of Birth"
                      value={dobDate}
                      onChange={(newValue) => {
                        setDobDate(newValue);
                        setSelectedDocument(null);
                      }}
                      slots={{
                        textField: TextField
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: { mb: 0 }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <StyledDatePicker
                      label="Admit Date"
                      value={admitDate}
                      onChange={(newValue) => {
                        setAdmitDate(newValue);
                        setSelectedDocument(null);
                      }}
                      slots={{
                        textField: TextField
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: { mb: 0 }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
                
                <FormControl fullWidth size="small" sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    height: '40px !important',
                    minHeight: '40px !important'
                  },
                  '& .MuiInputBase-root': {
                    height: '40px !important',
                    minHeight: '40px !important'
                  },
                  '& .MuiSelect-select': {
                    height: 'auto !important',
                    padding: '8.5px 14px !important'
                  }
                }}>
                  <InputLabel>Hospital</InputLabel>
                  <Select
                    value={hospital}
                    label="Hospital"
                    onChange={(e) => {
                      setHospital(e.target.value);
                      setSelectedDocument(null);
                    }}
                  >
                    <MenuItem value="">All Hospitals</MenuItem>
                    <MenuItem value="christ">Christ (Jersey City)</MenuItem>
                    <MenuItem value="humc">HUMC (Hoboken)</MenuItem>
                    <MenuItem value="bayonne">Bayonne</MenuItem>
                  </Select>
                </FormControl>
                
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <SearchButton type="submit" variant="contained" fullWidth>
                      Search
                    </SearchButton>
                  </Grid>
                  <Grid item xs={4}>
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      onClick={handleResetSearch}
                      disabled={!isSearching}
                    >
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Patient Records */}
            <Typography variant="body2" sx={{ mb: 1 }}>
              Patient Records {totalPatients > 0 ? `(${totalPatients} found)` : ''}
            </Typography>
            
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ ml: 2 }}>Searching patients...</Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {patients.map((patient) => (
                  <React.Fragment key={patient.id}>
                    <ListItem 
                      sx={{
                        flexDirection: 'column', 
                        alignItems: 'flex-start',
                        bgcolor: selectedPatient?.id === patient.id ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => handlePatientSelect(patient)}
                    >
                      <ListItemText
                        primary={`${patient.first_name} ${patient.last_name}`}
                        secondary={<>
                          <Typography variant="body2" component="span" display="block">
                            DOB: {patient.dob ? formatDate(patient.dob) : 'N/A'}
                          </Typography>
                          <Typography variant="body2" component="span" display="block">
                            Admit date: {patient.admit_date ? formatDate(patient.admit_date) : 'N/A'}
                          </Typography>
                          <Typography variant="body2" component="span" display="block">
                            {getPatientHospital(patient)}
                          </Typography>
                        </>}
                        primaryTypographyProps={{ fontWeight: 'bold' }}
                      />
                      
                      {selectedPatient?.id === patient.id && (
                        <Box sx={{ width: '100%', pl: 2, mt: 1 }}>
                          <PatientDocuments 
                            patientId={patient.id} 
                            onSelectDocument={handleDocumentSelect}
                            selectedDocument={selectedDocument}
                          />
                        </Box>
                      )}
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
                {patients.length === 0 && (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                    No patients found
                  </Typography>
                )}
              </List>
              )}
            </Box>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Stack spacing={2} alignItems="center" sx={{ mt: 2, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Pagination 
                  count={totalPages} 
                  page={isSearching ? currentPage : page} 
                  onChange={handlePageChange} 
                  color="primary"
                  size="small"
                />
              </Stack>
            )}
          </Paper>
        </Grid>
        
        {/* Right Panel - Document Viewer */}
        <Grid item xs={12} md={8} sx={{ height: '100%' }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            {isAdmin && (
              <FormControlLabel
                control={<Switch checked={showSanduke} onChange={(e) => setShowSanduke(e.target.checked)} name="showSanduke" />}
                label="Show 'sanduke' Scans"
                sx={{ mb: 1, display: 'block' }}
              />
            )}
            <Typography variant="h6" gutterBottom>Document Viewer</Typography>
            <Typography variant="body2" gutterBottom color="text.secondary">
              {selectedDocument ? 
                `Viewing: ${selectedDocument.file_name || `Document #${selectedDocument.id}`}` : 
                'Select a patient to view documents'}
            </Typography>
            
            <Box sx={{ mt: 2, height: 'calc(100% - 80px)' }}>
              <DocumentViewer document={selectedDocument} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
    </LocalizationProvider>
  );
};

export default PatientScans;
